import { z } from 'zod';

/**
 * How a Brief's numbers stay true. A synthesis never writes a count: it writes a token naming the
 * count, and the number shown is the evidence's. Every other number in what it writes is refused,
 * so a Brief's prose holds no figure that is not a count of people from the evidence, and a quote is
 * never written by the synthesis at all — it is named by handle, and its words come from the
 * evidence.
 *
 * The tokens:
 *
 * - `{{count:POLL3.2}}` — how many people chose option 2 of poll 3.
 * - `{{of:POLL3}}` — how many people answered poll 3.
 * - `{{count:THEME2}}` — how many people theme 2 counts, once each.
 * - `{{option:POLL3.2}}` — option 2's wording, as the guide has it, so an option such as "$4.99" or
 *   "3 times a week" never needs a numeral in prose.
 */
export const BRIEF_TOKEN_KINDS = ['count', 'of', 'option'] as const;
export type BriefTokenKind = (typeof BRIEF_TOKEN_KINDS)[number];

/** A token as written, before its handle is resolved against any evidence. */
export type BriefToken =
  | { kind: 'count'; poll: number; option: number }
  | { kind: 'count'; theme: number }
  | { kind: 'of'; poll: number }
  | { kind: 'option'; poll: number; option: number };

const TOKEN = /\{\{(count|of|option):(POLL|THEME)([1-9]\d*)(?:\.([1-9]\d*))?\}\}/g;

/** Writes a token, so a fixture and a test spell it the way the grammar reads it. */
export const briefToken = (token: BriefToken): string => {
  if ('theme' in token) {
    return `{{count:THEME${String(token.theme)}}}`;
  }
  if (token.kind === 'of') {
    return `{{of:POLL${String(token.poll)}}}`;
  }
  return `{{${token.kind}:POLL${String(token.poll)}.${String(token.option)}}}`;
};

const readToken = (
  kind: string,
  family: string,
  number: number,
  option: number | undefined,
): BriefToken | null => {
  if (family === 'THEME') {
    return kind === 'count' && option === undefined ? { kind: 'count', theme: number } : null;
  }
  if (kind === 'of') {
    return option === undefined ? { kind: 'of', poll: number } : null;
  }
  if (option === undefined) {
    return null;
  }
  return kind === 'count'
    ? { kind: 'count', poll: number, option }
    : { kind: 'option', poll: number, option };
};

/**
 * A kind of token one grammar reads beside the Brief's own, which no Brief may hold: `pattern`
 * finds it (its source is matched alongside the Brief's tokens, so it takes no flags), and `read`
 * takes the whole token as written, answering `null` for one that is malformed.
 */
export interface BriefProseExtension<Extra> {
  pattern: RegExp;
  read: (token: string) => Extra | null;
}

/**
 * Prose cut at its tokens: the words between them, and each token where it stood — a Brief token,
 * or, under an extension, one of the extension's.
 */
export type BriefProsePart<Extra = never> =
  | { t: 'text'; text: string }
  | { t: 'token'; token: BriefToken }
  | ([Extra] extends [never] ? never : { t: 'extra'; token: Extra });

/**
 * Prose split into words and tokens, or `null` when anything in it looks like a token and is not
 * one — an unknown kind, a poll without its option, a stray `{{` or `}}` — since a reader would see
 * the braces where a number belongs. With an extension its tokens are read too; without one, they
 * are braces like any other.
 */
export const splitBriefProse = <Extra = never>(
  prose: string,
  extension?: BriefProseExtension<Extra>,
): BriefProsePart<Extra>[] | null => {
  const parts: BriefProsePart<Extra>[] = [];
  const tokens =
    extension === undefined
      ? TOKEN
      : new RegExp(
          `${TOKEN.source}|${extension.pattern.source}`,
          TOKEN.flags.includes('g') ? TOKEN.flags : `${TOKEN.flags}g`,
        );
  let at = 0;

  for (const match of prose.matchAll(tokens)) {
    const [whole, kind, family, number, option] = match;
    let part: BriefProsePart<Extra>;
    if (kind === undefined && extension !== undefined) {
      const token = extension.read(whole);
      if (token === null) {
        return null;
      }
      part = { t: 'extra', token } as BriefProsePart<Extra>;
    } else {
      const token = readToken(
        kind as string,
        family as string,
        Number(number),
        option === undefined ? undefined : Number(option),
      );
      if (token === null) {
        return null;
      }
      part = { t: 'token', token };
    }
    if (match.index > at) {
      parts.push({ t: 'text', text: prose.slice(at, match.index) });
    }
    parts.push(part);
    at = match.index + whole.length;
  }

  if (at < prose.length) {
    parts.push({ t: 'text', text: prose.slice(at) });
  }

  const stray = parts.some(
    (part) => part.t === 'text' && (part.text.includes('{{') || part.text.includes('}}')),
  );

  return stray ? null : parts;
};

/** One numeral found in prose: where it is, and what it says. */
export interface BareNumeral {
  start: number;
  end: number;
  text: string;
}

/**
 * Number words. `one` is not here: alone it is how a sentence says "a person", and it is refused
 * only as part of a larger number ("one in five", "one hundred", "one third"). Neither are the
 * ordinals — "first", "second" and "the third question" count nobody.
 */
const CARDINALS = new Set([
  'zero',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
  'hundred',
  'hundreds',
  'thousand',
  'thousands',
  'million',
  'millions',
  'billion',
  'billions',
  'dozen',
  'dozens',
  'half',
  'halves',
  'percent',
  // A quarter of something is a count, whatever comes before it; "the third question" is not.
  'quarter',
  // Multipliers: "twice as many" says how many as surely as a number does.
  'twice',
  'thrice',
  'double',
  'doubled',
  'triple',
  'tripled',
  'quadruple',
  'quadrupled',
]);

const TENS = new Set([
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
]);
const SCALES = new Set(['hundred', 'thousand', 'million', 'billion']);

/** A fraction's denominator: a number in the plural, an ordinal alone ("the third question"). */
const DENOMINATORS = new Set([
  'third',
  'quarter',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
]);
const PLURAL_DENOMINATORS = new Set([...DENOMINATORS].map((word) => `${word}s`));

interface Atom {
  kind: 'digits' | 'word' | 'percent' | 'dash';
  start: number;
  end: number;
  word: string;
  /** Whether only spaces separate this atom from the one before it. */
  joined: boolean;
}

/**
 * Digits of any script, and words of any script with their combining marks and invisible format
 * characters inside them, so a numeral written in fullwidth or Arabic-Indic digits, or a number word
 * split by a zero-width space or an accent, is still one atom. A word is classified with its marks
 * and format characters taken out (`bareWord`).
 */
const ATOM =
  /(\p{N}(?:\p{Cf}*\p{N})*(?:[.,:/]\p{N}(?:\p{Cf}*\p{N})*)*(?:st|nd|rd|th|s)?)|(\p{L}[\p{L}\p{M}\p{Cf}]*)|(%)|([-–—‐‑‒−―])/gu;

/** A word as it reads: every combining mark and format character taken out. */
const bareWord = (text: string): string => text.normalize('NFKD').replace(/[\p{M}\p{Cf}]/gu, '');

/**
 * A capital Roman numeral from two to thirty-nine, two letters or more — `II`, `IV`, `XII` — which
 * reads as a number. Larger ones are left alone: `CD`, `DC`, `MD` and `XL` are far more often
 * something else.
 */
const ROMAN = /^(?=[IVX]{2})X{0,3}(?:IX|IV|V?I{0,3})$/;

/** A capital letter, then capitals and digits, four characters at most, with exactly one digit. */
const IDENTIFIER = /^(?=[A-Z0-9]{1,4}$)[A-Z][A-Z]*[0-9][A-Z]*$/;

/** A run of letters made only of Roman numeral letters, which could be one. */
const ROMAN_LETTERS = /^[IVXLCDM]+$/;

/**
 * Whether a letters-and-digits token is a short identifier — `Q3`, `MP3`, `B2B`, `H1` — which names
 * a thing rather than counting anybody, so nothing in it is a numeral. It must match `IDENTIFIER`,
 * and no run of its letters may be a number word or Roman numeral letters, so `Two5`, `IV2`, `X9`,
 * `P50`, `N12`, `Q10`, `iOS17` and `b2b` are not identifiers, and neither is any other digit run
 * joined to letters: `participants42`, `Microsoft365`, `3D`, `10am`, `2x`.
 */
const isIdentifier = (token: string): boolean =>
  IDENTIFIER.test(token) &&
  token
    .split(/[0-9]/)
    .filter((letters) => letters !== '')
    .every(
      (letters) =>
        !ROMAN_LETTERS.test(letters) &&
        !CARDINALS.has(letters.toLowerCase()) &&
        letters.toLowerCase() !== 'one',
    );

/**
 * Where each identifier (`isIdentifier`) sits in `text`, in order: found in one pass over its
 * letter-and-digit runs, so deciding whether an atom is inside one never re-reads the text around
 * it.
 */
const identifierSpans = (text: string): { from: number; to: number }[] =>
  [...text.matchAll(/[A-Za-z0-9]+/g)]
    .filter(([token]) => isIdentifier(token))
    .map((match) => ({ from: match.index, to: match.index + match[0].length }));

/**
 * The atoms of `text`, in one pass: every pattern here is matched without backtracking across the
 * text, and every check on an atom looks only at the atom or at spans found up front, so the work
 * is linear in the text's length.
 */
const atomsOf = (text: string): Atom[] => {
  const atoms: Atom[] = [];
  const identifiers = identifierSpans(text);
  let identifier = 0;
  let previousEnd = 0;
  for (const match of text.matchAll(ATOM)) {
    const [whole, digits, word, percent] = match;
    const start = match.index;
    while ((identifiers[identifier]?.to ?? Infinity) <= start) identifier += 1;
    const span = identifiers[identifier];
    if (percent === undefined && span !== undefined && span.from <= start) {
      previousEnd = start + whole.length;
      continue;
    }
    atoms.push({
      kind:
        digits !== undefined
          ? 'digits'
          : word !== undefined
            ? 'word'
            : percent !== undefined
              ? 'percent'
              : 'dash',
      start,
      end: start + whole.length,
      word: ROMAN.test(bareWord(whole)) ? 'roman' : bareWord(whole).toLowerCase(),
      joined: atoms.length > 0 && /^\s*$/.test(text.slice(previousEnd, start)),
    });
    previousEnd = start + whole.length;
  }
  return atoms;
};

const isNumber = (atom: Atom | undefined): boolean =>
  atom !== undefined &&
  (atom.kind === 'digits' ||
    atom.kind === 'percent' ||
    (atom.kind === 'word' &&
      (CARDINALS.has(atom.word) || PLURAL_DENOMINATORS.has(atom.word) || atom.word === 'roman')));

const isWord = (atom: Atom | undefined, ...words: string[]): boolean =>
  atom !== undefined && atom.kind === 'word' && atom.joined && words.includes(atom.word);

/** What may follow "a" or "an" to make a number of it: "a third", "a dozen", "a couple". */
const AFTER_A = new Set([
  ...DENOMINATORS,
  'half',
  'dozen',
  'couple',
  'hundred',
  'thousand',
  'million',
  'billion',
]);

/** Where a numeral starts at `index`, or `-1`; then how far it runs. */
const numeralAt = (atoms: readonly Atom[], index: number): number => {
  const atom = atoms[index] as Atom;
  const next = atoms[index + 1];
  const joinedNext = next !== undefined && next.joined;

  const proportion = (from: number): boolean =>
    (isWord(atoms[from], 'in') &&
      atoms[from + 1]?.joined === true &&
      isNumberOrOne(atoms[from + 1])) ||
    (isWord(atoms[from], 'out') &&
      isWord(atoms[from + 1], 'of') &&
      atoms[from + 2]?.joined === true &&
      isNumberOrOne(atoms[from + 2]));

  if (atom.kind === 'word' && (atom.word === 'a' || atom.word === 'an')) {
    return joinedNext && next.kind === 'word' && AFTER_A.has(next.word) ? index : -1;
  }
  if (atom.kind === 'word' && atom.word === 'one') {
    const compound =
      joinedNext &&
      next.kind === 'word' &&
      (DENOMINATORS.has(next.word) || PLURAL_DENOMINATORS.has(next.word) || SCALES.has(next.word));
    const hyphened =
      joinedNext &&
      next.kind === 'dash' &&
      atoms[index + 2]?.joined === true &&
      (isNumber(atoms[index + 2]) || DENOMINATORS.has(atoms[index + 2]?.word ?? ''));
    return compound || hyphened || proportion(index + 1) ? index : -1;
  }
  if (atom.kind === 'word' && atom.word === 'per') {
    return isWord(next, 'cent') ? index : -1;
  }
  return isNumber(atom) ? index : -1;
};

const isNumberOrOne = (atom: Atom | undefined): boolean =>
  isNumber(atom) || (atom !== undefined && atom.kind === 'word' && atom.word === 'one');

/** The index of the last atom of the numeral that starts at `index`. */
const numeralEnd = (atoms: readonly Atom[], index: number): number => {
  let at = index;
  for (;;) {
    const current = atoms[at] as Atom;
    const next = atoms[at + 1];
    if (next === undefined || !next.joined) {
      return at;
    }
    const after = atoms[at + 2];

    if (next.kind === 'dash' && after?.joined === true) {
      if (isNumberOrOne(after) || DENOMINATORS.has(after.word)) {
        at += 2;
        continue;
      }
      return at;
    }
    if (isNumber(next) || (next.kind === 'word' && DENOMINATORS.has(next.word))) {
      at += 1;
      continue;
    }
    if (next.word === 'one' && (TENS.has(current.word) || SCALES.has(current.word))) {
      at += 1;
      continue;
    }
    if (next.word === 'cent' && current.word === 'per') {
      at += 1;
      continue;
    }
    if (next.word === 'per' && isWord(after, 'cent')) {
      at += 2;
      continue;
    }
    if (next.word === 'couple' && (current.word === 'a' || current.word === 'an')) {
      at += 1;
      continue;
    }
    if (next.word === 'of' && current.word === 'couple') {
      at += 1;
      continue;
    }
    if (
      next.word === 'and' &&
      SCALES.has(current.word) &&
      after?.joined === true &&
      isNumberOrOne(after)
    ) {
      at += 2;
      continue;
    }
    if (next.word === 'in' && after?.joined === true && isNumberOrOne(after)) {
      at += 2;
      continue;
    }
    if (
      next.word === 'out' &&
      isWord(after, 'of') &&
      atoms[at + 3]?.joined === true &&
      isNumberOrOne(atoms[at + 3])
    ) {
      at += 3;
      continue;
    }
    if (
      next.word === 'to' &&
      current.kind === 'digits' &&
      after?.joined === true &&
      after.kind === 'digits'
    ) {
      at += 2;
      continue;
    }
    return at;
  }
};

/**
 * Every numeral in prose, each once and whole: digit runs with their separators, decimals and
 * ordinal endings (`1,200`, `2.5`, `2nd`), ratios (`3:1`, `3/5`), ranges (`10-15`, `10 to 15`),
 * percentages (`40%`, `forty per cent`), spelled numbers from zero up (`twenty-three`,
 * `two hundred and five`), fractions and proportions (`half`, `a third`, `two thirds`,
 * `one in five`, `3 out of 5`), multipliers (`twice`, `double`, `triple`), `a dozen`, `a couple
 * of`, capital Roman numerals (`XII`), and digits and number words of any script or case, with any
 * mark or invisible character inside them. Not `one` alone, `no one`, `none`, the ordinals, which
 * count nobody, or the digits of a short identifier (`Q3`, `MP3`, `B2B`, `H1`), which name a thing.
 */
export const findBareNumerals = (text: string): BareNumeral[] => {
  const atoms = atomsOf(text);
  const found: BareNumeral[] = [];

  for (let index = 0; index < atoms.length; index += 1) {
    const start = numeralAt(atoms, index);
    if (start === -1) {
      continue;
    }
    const last = numeralEnd(atoms, start);
    const from = (atoms[start] as Atom).start;
    const to = (atoms[last] as Atom).end;
    found.push({ start: from, end: to, text: text.slice(from, to) });
    index = last;
  }

  return found;
};

/** The fewest words a double-quoted passage may hold before prose reads as quoting someone. */
export const QUOTED_SPAN_MIN_WORDS = 4;

/** A passage between any two quotation marks with none inside it; group 1 is what it holds. */
const QUOTED_PASSAGE = /["“”„‟«»]([^"“”„‟«»]*)["“”„‟«»]/g;

/** Whether what a passage in quotation marks holds is `QUOTED_SPAN_MIN_WORDS` words or more. */
const quotesSomeone = (inside: string): boolean =>
  (inside.match(/[\p{L}\p{N}']+/gu) ?? []).length >= QUOTED_SPAN_MIN_WORDS;

/**
 * Every double-quoted passage of `QUOTED_SPAN_MIN_WORDS` words or more. A Brief quotes a
 * participant only by handle, so a passage in quotation marks is the synthesis writing someone's
 * words itself, which it may not: a quote it wrote could be a paraphrase, and its numbers are
 * nobody's.
 */
export const findQuotedSpans = (text: string): string[] =>
  [...text.matchAll(QUOTED_PASSAGE)].map((match) => match[1] ?? '').filter(quotesSomeone);

/**
 * `text` with every passage `findQuotedSpans` finds, its quotation marks included, put as one
 * space, so what is left is read exactly where the gate reads no quotation.
 */
export const withoutQuotedSpans = (text: string): string =>
  text.replace(QUOTED_PASSAGE, (passage, inside: string) =>
    quotesSomeone(inside) ? ' ' : passage,
  );

/**
 * Whether a character may appear in prose a synthesis wrote, once it is in NFKC: Latin letters and
 * no other script, and no control, format, private-use or unassigned character, combining mark, or
 * space but the plain one and the line break.
 */
const isProseCharacter = (character: string): boolean =>
  character === ' ' ||
  character === '\n' ||
  (!/[\p{C}\p{M}\p{Z}]/u.test(character) &&
    (!/\p{L}/u.test(character) || /\p{Script=Latin}/u.test(character)));

/**
 * Prose a synthesis wrote in the one form every later step reads. Any numeral but an ASCII digit is
 * refused outright — fullwidth, superscript, Arabic-Indic, a vulgar fraction, a Roman numeral sign.
 * The rest is put in NFKC, so ligatures and no-break spaces become what they look like, and then
 * held to `isProseCharacter`, so nothing is left that the gate could read one way and a
 * reader another — a digit of another script, a letter that only looks Latin, an invisible
 * character inside a word. `null` when anything is outside it; the caller refuses the passage, and
 * keeps this form, never the original, so what is gated is what is stored and shown.
 */
export const canonicalBriefProse = (prose: string): string | null => {
  // A numeral character of any other kind is refused before the normal form can spell it in
  // letters: NFKC reads a small Roman eight as `viii`, which is no longer a numeral to the gate.
  if (/[^\P{N}0-9]/u.test(prose)) {
    return null;
  }
  const canonical = prose.normalize('NFKC');

  return [...canonical].every(isProseCharacter) ? canonical : null;
};

/** The words a reader sees around the tokens: each token stands in as a word, so a quotation
 * opened before a token and closed after it is still one passage. */
const readAsWords = <Extra>(parts: readonly BriefProsePart<Extra>[]): string =>
  parts.map((part) => (part.t === 'text' ? part.text : 'N')).join('');

/**
 * The longest passage a synthesis may hand the gate, tokens included. Far above anything a Brief
 * shows — words past `MAX_BRIEF_PASSAGE_CHARS` are cut anyway — and checked before any other work,
 * so no step of the gate runs on an unbounded string.
 */
export const MAX_BRIEF_PROSE_INPUT_CHARS = 16_000;

/**
 * The prose's words, split from its tokens, when they may be shown: canonical
 * (`canonicalBriefProse`), every token well formed, no numeral in its words, and no quoted passage
 * in it as a reader sees it, tokens and all. `null` otherwise. Nothing is rewritten beyond the
 * canonical form — prose that fails is refused whole. An extension's tokens are read as tokens,
 * under the same gate; without one, the Brief's tokens are the only ones.
 */
export const gatedBriefProse = <Extra = never>(
  prose: string,
  extension?: BriefProseExtension<Extra>,
): BriefProsePart<Extra>[] | null => {
  if (prose.length > MAX_BRIEF_PROSE_INPUT_CHARS) {
    return null;
  }
  const canonical = canonicalBriefProse(prose);
  const parts = canonical === null ? null : splitBriefProse(canonical, extension);
  if (parts === null) {
    return null;
  }

  const numeral = parts.some((part) => part.t === 'text' && findBareNumerals(part.text).length > 0);

  return numeral || findQuotedSpans(readAsWords(parts)).length > 0 ? null : parts;
};

/** Whether prose a synthesis wrote may be shown (`gatedBriefProse`). */
export const passesNumeralGate = (prose: string): boolean => gatedBriefProse(prose) !== null;

/** The longest run of words a stored segment holds. A storage bound. */
export const MAX_RICH_TEXT_SEGMENT_CHARS = 4000;

/** The most segments one stored passage holds. A storage bound. */
export const MAX_RICH_TEXT_SEGMENTS = 400;

/** What a count counts: one option of a poll, or one theme. */
export const countRefSchema = z.union([
  z.strictObject({ poll: z.uuid(), option: z.uuid() }),
  z.strictObject({ theme: z.string().min(1) }),
]);
export type CountRef = z.infer<typeof countRefSchema>;

/**
 * A stored passage of a Brief, as segments: the synthesis's words, and each count, total and option
 * wording with what it names and the value the evidence gave it. Stored this way rather than as a
 * string so a number stays bound to what it counts — an edit to the words can be held to the words,
 * and no edit can change a number.
 */
export const richTextSegmentSchema = z.discriminatedUnion('t', [
  z.strictObject({
    t: z.literal('text'),
    text: z.string().min(1).max(MAX_RICH_TEXT_SEGMENT_CHARS),
  }),
  z.strictObject({ t: z.literal('count'), ref: countRefSchema, value: z.int().nonnegative() }),
  z.strictObject({ t: z.literal('of'), poll: z.uuid(), value: z.int().nonnegative() }),
  z.strictObject({
    t: z.literal('option'),
    poll: z.uuid(),
    option: z.uuid(),
    text: z.string().min(1).max(MAX_RICH_TEXT_SEGMENT_CHARS),
  }),
]);
export type RichTextSegment = z.infer<typeof richTextSegmentSchema>;

export const richTextSchema = z.array(richTextSegmentSchema).min(1).max(MAX_RICH_TEXT_SEGMENTS);
export type RichText = z.infer<typeof richTextSchema>;

/** A passage as a reader sees it: the words, with each count and option wording in its place. */
export const renderRichText = (text: RichText): string =>
  text
    .map((segment) => {
      switch (segment.t) {
        case 'text':
          return segment.text;
        case 'count':
        case 'of':
          return String(segment.value);
        case 'option':
          return segment.text;
      }
    })
    .join('');
